import { useMemo, useState } from 'react'
import { SYRINGES, DEFAULT_SYRINGE } from '../lib/peptide.js'

/*
 * Blend calculator — one vial containing MULTIPLE peptides in shared water
 * (e.g. Tirz/Reta 30/20 mg in 2 mL). Given how many units you draw, it shows how
 * much of each compound that draw contains. All syringes are U-100 (100 u = 1 mL),
 * so for a draw of U units the volume is U/100 mL and each compound scales by its
 * own concentration (mg ÷ water).
 */
let _id = 0
const newComp = (c = {}) => ({ id: ++_id, name: '', mg: '', ...c })
const round = (n, d = 2) => (Number.isFinite(n) ? Math.round(n * 10 ** d) / 10 ** d : n)
const fmtAmt = (mg) => (!Number.isFinite(mg) ? '—' : mg >= 1 ? `${round(mg, 2)} mg` : `${round(mg * 1000, 1)} mcg`)

export default function BlendCalc() {
  const [comps, setComps] = useState([newComp({ name: 'BPC-157' }), newComp({ name: 'TB-500' })])
  const [water, setWater] = useState('')
  const [syringe, setSyringe] = useState(DEFAULT_SYRINGE)
  const [units, setUnits] = useState('')

  const cap = SYRINGES.find((s) => s.id === syringe)?.capacity ?? 100
  const w = parseFloat(water)
  const u = parseFloat(units)
  const draw = Number.isFinite(u) && u > 0 ? u : 0

  const addComp = () => setComps((x) => (x.length < 5 ? [...x, newComp()] : x))
  const removeComp = (id) => setComps((x) => (x.length > 1 ? x.filter((c) => c.id !== id) : x))
  const setComp = (id, k, v) => setComps((x) => x.map((c) => (c.id === id ? { ...c, [k]: v } : c)))

  const rows = useMemo(
    () =>
      comps.map((c) => {
        const mg = parseFloat(c.mg)
        if (!(mg > 0) || !(w > 0)) return { c, ok: false }
        const mgMl = mg / w // mg per mL
        const mgPerUnit = mgMl / 100 // U-100: 100 units = 1 mL
        return { c, ok: true, mgPerUnit, mgInDraw: mgPerUnit * draw }
      }),
    [comps, w, draw]
  )

  const hasComp = comps.some((c) => parseFloat(c.mg) > 0)
  const ready = w > 0 && hasComp && draw > 0
  const dosesPerVial = ready ? Math.floor((w * 100) / draw) : null
  const overCap = draw > cap

  return (
    <div className="blend">
      <div className="card card-pad">
        <label className="label">What’s in the vial <span className="hint">(each compound + its mg)</span></label>
        {comps.map((c, i) => (
          <div className="blend-comp" key={c.id}>
            <input className="input" placeholder={i === 0 ? 'e.g. BPC-157' : 'e.g. TB-500'}
              value={c.name} onChange={(e) => setComp(c.id, 'name', e.target.value)} />
            <div className="input-group blend-mg">
              <input className="input" type="number" min="0" step="any" inputMode="decimal" placeholder="10"
                value={c.mg} onChange={(e) => setComp(c.id, 'mg', e.target.value)} />
              <span className="addon">mg</span>
            </div>
            {comps.length > 1 && (
              <button type="button" className="extra-remove" onClick={() => removeComp(c.id)} aria-label="Remove">✕</button>
            )}
          </div>
        ))}
        {comps.length < 5 && (
          <button type="button" className="btn btn-ghost btn-sm add-extra" onClick={addComp}>＋ Add compound</button>
        )}

        <div className="field" style={{ marginTop: 16 }}>
          <label className="label">Bacteriostatic water <span className="hint">(added to the vial)</span></label>
          <div className="input-group">
            <input className="input" type="number" min="0" step="any" inputMode="decimal" placeholder="e.g. 2"
              value={water} onChange={(e) => setWater(e.target.value)} />
            <span className="addon">mL</span>
          </div>
        </div>

        <div className="field">
          <label className="label">Units you draw <span className="hint">(on the syringe)</span></label>
          <div className="input-group">
            <input className="input" type="number" min="0" step="any" inputMode="decimal" placeholder="e.g. 20"
              value={units} onChange={(e) => setUnits(e.target.value)} />
            <span className="addon">units</span>
          </div>
        </div>

        <div className="field mb-0">
          <label className="label">Syringe size <span className="hint">(U-100 insulin)</span></label>
          <div className="seg" style={{ width: '100%' }}>
            {SYRINGES.map((s) => (
              <button key={s.id} type="button" style={{ flex: 1 }} className={syringe === s.id ? 'active' : ''}
                onClick={() => setSyringe(s.id)}>
                <span style={{ fontWeight: 700 }}>{s.capacity}u</span>
                <span className="muted" style={{ fontSize: '.72rem', display: 'block' }}>{s.ml}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="card result-card blend-result">
        {ready ? (
          <div className="fade-in">
            <div className="result-hero">
              <div className="result-hero-label">Each draw of {round(draw, 2)} units contains</div>
              <div className="blend-breakdown">
                {rows.map((r) => (
                  <div className="blend-row" key={r.c.id}>
                    <span className="blend-name">{r.c.name?.trim() || 'Compound'}</span>
                    <span className="blend-amt">{r.ok ? fmtAmt(r.mgInDraw) : '—'}</span>
                  </div>
                ))}
              </div>
            </div>

            {overCap && (
              <div className="warn">⚠️ {round(draw, 2)} units is more than a {cap}-unit syringe holds. Use a larger syringe or draw less.</div>
            )}

            <div className="directions">
              <div className="directions-title">📋 Per unit</div>
              <p style={{ margin: 0 }}>
                {rows.filter((r) => r.ok).map((r, i) => (
                  <span key={r.c.id}>{i > 0 ? ' · ' : ''}<strong>{fmtAmt(r.mgPerUnit)}</strong> {r.c.name?.trim() || 'compound'} / unit</span>
                ))}
              </p>
            </div>

            <div className="result-grid" style={{ gridTemplateColumns: '1fr' }}>
              <div className="stat">
                <div className="stat-label">Full-vial draws</div>
                <div className="stat-value">{dosesPerVial}<span className="stat-unit"> × {round(draw, 2)} u</span></div>
              </div>
            </div>
          </div>
        ) : (
          <div className="result-empty">
            <p className="muted center" style={{ maxWidth: 320, margin: '0 auto' }}>
              Enter each compound’s mg, the water you added, and how many units you draw — we’ll show how much of
              each peptide is in that draw.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
